import { Directive, OnInit, effect, input, output, signal } from '@angular/core';
import { linkedItemsHelper } from 'lib/helpers/linked-items-helper.class.js';
import { CosmosDb } from '../../../../lib/cosmos/cosmosDb.js';
import { CoreAppComponent } from '../../core/core-app.component.js';
import { ISharedData } from './type-route.models.js';

@Directive()
export abstract class TypeCoreComponent<TData = undefined> extends CoreAppComponent implements OnInit {
	public readonly inputData = input<ISharedData<TData> | undefined>();

	private dataInitialized: boolean = false;
	private sharedDataInitProcessed: boolean = false;

	protected sharedData = signal<ISharedData<TData> | undefined>(undefined);
	protected dataInit?: () => void;
	protected sharedDataInit?: (data: ISharedData<TData>) => void;

	public readonly updateSharedDataOutput = output<ISharedData<TData>>();

	constructor() {
		super();

		effect(() => {
			const inputSharedData = this.inputData();

			if (inputSharedData?.data) {
				this.sharedData.set(inputSharedData);

				if (!this.sharedDataInitProcessed && this.sharedDataInit) {
					this.sharedDataInitProcessed = true;
					this.sharedDataInit(inputSharedData);
				}
			}
		});
	}

	ngOnInit(): void {
		this.runDataInit();
	}

	private runDataInit(): void {
		if (this.dataInit && !this.dataInitialized) {
			// only init once
			this.dataInitialized = false;
			this.dataInit();
		}
	}

	protected updateSharedData(sharedData: ISharedData<TData>): void {
		this.updateSharedDataOutput.emit(sharedData);
		this.sharedData.set(sharedData);
	}

	protected updateData(newData: TData): void {
		const sharedData = this.sharedData();

		if (sharedData) {
			this.updateSharedData({
				data: newData,
				linkedItems: sharedData.linkedItems
			});
		}
	}

	protected getLinkedItemById<TItem extends CosmosDb.ICosmosItem>(id: string): TItem | undefined {
		return linkedItemsHelper.getLinkedItemById(this.sharedData()?.linkedItems ?? [], id);
	}

	protected getLinkedItemsByType<TItem extends CosmosDb.ICosmosItem>(
		type: CosmosDb.CosmosContainerType
	): readonly TItem[] {
		return linkedItemsHelper.getLinkedItemsByType(this.sharedData()?.linkedItems ?? [], type);
	}

	protected addLinkedItemToSharedData<TItem extends CosmosDb.ICosmosItem>(item: TItem): void {
		const sharedData = this.sharedData();

		if (sharedData) {
			if (!sharedData.linkedItems) {
				sharedData.linkedItems = [];
			}
			sharedData.linkedItems.push(item);
			this.updateSharedData(sharedData);
		}
	}
}
